/**
 * AlgorithmCardBar — Bottom-center horizontal bar with 6 algorithm-card slots.
 *
 * Each slot shows the card name, tier colour, hotkey number, and a conic-gradient
 * cooldown overlay. Players activate cards with keys 1-6 or by clicking.
 */

import { getAlgorithmCardVisual } from '../rendering/algorithmCardAssets';
import { ALGORITHM_CARDS, type AlgorithmCardType } from '../../shared/types';
import { RangeIndicatorRenderer } from './RangeIndicatorRenderer';
import type { Vec3 } from '../../shared/utils';

const ALGORITHM_CARD_NAMES: Record<number, string> = {
  0: 'Regroup',
  1: 'Scatter',
  2: 'Follow',
  3: 'Orbit',
  4: 'Defensive Wall',
  5: 'Radar Ring',
  6: 'Focus Fire',
  7: 'Relay Net',
  8: 'Cage Surround',
  9: 'Sync Dive',
  10: 'Convert Shot',
  11: 'Choreo Ultimate',
  12: 'Horizontal Orbit',
  13: 'Diagonal Orbit',
};

const TIER_NAMES = ['common', 'rare', 'legend'] as const;
const TIER_LABELS = ['Common', 'Rare', 'Legend'] as const;
const SLOT_COUNT = 6;

/** Duration of the golden cooldown-ready pulse class on a card icon. */
export const READY_FLASH_MS = 200;

export interface CardSlotData {
  slotIndex: number;
  cardType: number;
  tier: number;
  cooldownFraction: number;
  active: boolean;
}

export class AlgorithmCardBar {
  private container: HTMLElement;
  private slots: HTMLElement[] = [];
  private activateCb: ((slot: number) => void) | null = null;
  private discardCb: ((slot: number) => void) | null = null;
  /**
   * Per-slot rising-edge tracker for the cooldown fraction. `null` means
   * "not yet observed" — the very first sample on each slot does NOT
   * trigger a flash (cards arriving with cooldown 0 must not pulse).
   */
  private prevCardCooldowns: Array<number | null> = new Array(SLOT_COUNT).fill(null);
  private flashTimers: Array<ReturnType<typeof setTimeout> | null> = new Array(SLOT_COUNT).fill(
    null,
  );
  private rangeIndicator: RangeIndicatorRenderer | null = null;
  /** Latest swarm centroid pushed in via `setSwarmCentroid()` (or origin). */
  private swarmCentroid: Vec3 = { x: 0, y: 0, z: 0 };
  /** Latest cardType array indexed by slot (for hover lookup). */
  private slotCardTypes: number[] = new Array(SLOT_COUNT).fill(-1);

  constructor(parentElement: HTMLElement) {
    this.container = document.createElement('div');
    this.container.className = 'card-bar hidden';

    for (let i = 0; i < SLOT_COUNT; i++) {
      const slot = this.createSlotElement(i);
      this.slots.push(slot);
      this.container.appendChild(slot);
    }

    parentElement.appendChild(this.container);

    // Range indicator lives in the same parent so it overlays the play area.
    this.rangeIndicator = new RangeIndicatorRenderer(parentElement);
    // Hotkeys 1-6 are owned exclusively by `KeyboardBindings`; routing them
    // through `InputManager → CommandType.ActivateCard` avoids the double
    // dispatch that an extra document listener here would create.
  }

  dispose(): void {
    for (const t of this.flashTimers) if (t) clearTimeout(t);
    this.flashTimers.fill(null);
    this.rangeIndicator?.dispose();
    this.rangeIndicator = null;
  }

  /**
   * Push the current swarm centroid into the bar so hover-range
   * indicators anchor to a meaningful world-space point. Optional —
   * defaults to origin when the caller does not supply one.
   */
  setSwarmCentroid(centroid: Vec3): void {
    this.swarmCentroid = { x: centroid.x, y: centroid.y, z: centroid.z };
  }

  /**
   * Test/integration hook: returns the underlying range indicator so
   * callers (and tests) can assert visibility / dispose behavior.
   */
  getRangeIndicator(): RangeIndicatorRenderer | null {
    return this.rangeIndicator;
  }

  /** Refresh all slots from state. */
  update(slotData: CardSlotData[]): void {
    const dataBySlot = new Map<number, CardSlotData>();
    for (const data of slotData) {
      dataBySlot.set(data.slotIndex, data);
    }

    for (let slotIndex = 0; slotIndex < SLOT_COUNT; slotIndex++) {
      const el = this.slots[slotIndex];
      const data = dataBySlot.get(slotIndex);
      if (!el) continue;

      const isEmpty = !data || data.cardType < 0;
      // Preserve `card--ready-flash` across className resets so the
      // 200ms pulse animation isn't immediately stripped on the next
      // update tick.
      const wasFlashing = el.classList.contains('card--ready-flash');
      el.className = 'card-slot';
      if (wasFlashing) el.classList.add('card--ready-flash');

      if (isEmpty) {
        el.classList.add('empty');
        this.setSlotContent(el, -1, '', '', 0);
        this.slotCardTypes[slotIndex] = -1;
        // Reset rising-edge tracker when the slot empties so a
        // refilled card doesn't immediately flash on first observation.
        this.prevCardCooldowns[slotIndex] = null;
        continue;
      }

      const tierKey = TIER_NAMES[data.tier] ?? 'common';
      el.classList.add(`tier-${tierKey}`);
      if (data.active) el.classList.add('active');

      const name = ALGORITHM_CARD_NAMES[data.cardType] ?? '???';
      const tierLabel = TIER_LABELS[data.tier] ?? 'Common';

      this.setSlotContent(el, data.cardType, name, tierLabel, data.cooldownFraction);

      // If the slot now holds a different card than last tick, the previous
      // cooldown belonged to the OLD card — comparing the new card's value
      // against it would fire a spurious ready-flash on swap-in.
      if (data.cardType !== this.slotCardTypes[slotIndex]) {
        this.prevCardCooldowns[slotIndex] = null;
      }
      this.slotCardTypes[slotIndex] = data.cardType;
      // Detect cooldown rising-edge: prev > 0 and now === 0 → ready flash.
      const prev = this.prevCardCooldowns[slotIndex];
      const cur = data.cooldownFraction;
      if (prev != null && prev > 0 && cur === 0) {
        this.triggerReadyFlash(slotIndex);
      }
      this.prevCardCooldowns[slotIndex] = cur;
    }
  }

  /**
   * Add the `card--ready-flash` class to the slot for `READY_FLASH_MS`,
   * then remove it. Idempotent: re-arming the flash mid-pulse just
   * resets the timer.
   */
  private triggerReadyFlash(slotIndex: number): void {
    const el = this.slots[slotIndex];
    if (!el) return;
    const existing = this.flashTimers[slotIndex];
    if (existing) clearTimeout(existing);
    el.classList.add('card--ready-flash');
    this.flashTimers[slotIndex] = setTimeout(() => {
      el.classList.remove('card--ready-flash');
      this.flashTimers[slotIndex] = null;
    }, READY_FLASH_MS);
  }

  /** Register callback for card activation (click / hotkey). */
  onCardActivate(cb: (slot: number) => void): void {
    this.activateCb = cb;
  }

  /** Register callback for card discard (small x button). */
  onCardDiscard(cb: (slot: number) => void): void {
    this.discardCb = cb;
  }

  show(): void {
    this.container.classList.remove('hidden');
  }

  hide(): void {
    this.container.classList.add('hidden');
  }

  private createSlotElement(index: number): HTMLElement {
    const slot = document.createElement('div');
    slot.className = 'card-slot empty';
    slot.dataset.slot = String(index);

    const hotkey = document.createElement('div');
    hotkey.className = 'card-hotkey';
    hotkey.textContent = String(index + 1);
    slot.appendChild(hotkey);

    const icon = document.createElement('img');
    icon.className = 'card-icon';
    icon.alt = '';
    icon.draggable = false;
    slot.appendChild(icon);

    const nameEl = document.createElement('div');
    nameEl.className = 'card-name';
    slot.appendChild(nameEl);

    const tierEl = document.createElement('div');
    tierEl.className = 'card-tier-label';
    slot.appendChild(tierEl);

    const cooldown = document.createElement('div');
    cooldown.className = 'card-cooldown';
    slot.appendChild(cooldown);

    const discard = document.createElement('div');
    discard.className = 'card-discard';
    discard.textContent = '\u00d7'; // multiplication sign (x)
    discard.addEventListener('click', (e) => {
      e.stopPropagation();
      this.discardCb?.(index);
    });
    slot.appendChild(discard);

    slot.addEventListener('click', () => {
      this.activateCb?.(index);
    });

    // Hover preview: show the AoE/influence range ring at the swarm
    // centroid; hide on leave. We look the radius up at hover-time so
    // a card swap into the slot picks up the new card's range. Cards
    // without a spatial range (e.g. Follow, Regroup) skip the ring —
    // showing it would falsely imply they're AoE.
    slot.addEventListener('mouseenter', () => {
      const cardType = this.slotCardTypes[index];
      if (cardType < 0) return;
      const def = ALGORITHM_CARDS[cardType as AlgorithmCardType];
      if (def?.range == null) return;
      this.rangeIndicator?.showRangeAt(this.swarmCentroid, def.range);
    });
    slot.addEventListener('mouseleave', () => {
      this.rangeIndicator?.hide();
    });

    return slot;
  }

  private setSlotContent(
    el: HTMLElement,
    cardType: number,
    name: string,
    tierLabel: string,
    cooldownFraction: number,
  ): void {
    const iconEl = el.querySelector('.card-icon') as HTMLImageElement;
    const nameEl = el.querySelector('.card-name') as HTMLElement;
    const tierEl = el.querySelector('.card-tier-label') as HTMLElement;
    const cooldownEl = el.querySelector('.card-cooldown') as HTMLElement;
    const discardEl = el.querySelector('.card-discard') as HTMLElement;

    if (cardType >= 0) {
      const visual = getAlgorithmCardVisual(cardType);
      if (iconEl.dataset.src !== visual.url) {
        iconEl.src = visual.url;
        iconEl.dataset.src = visual.url;
      }
      iconEl.style.display = '';
      el.style.setProperty('--card-accent', visual.accentColor);
      discardEl.style.display = '';
    } else {
      iconEl.removeAttribute('src');
      delete iconEl.dataset.src;
      iconEl.style.display = 'none';
      el.style.removeProperty('--card-accent');
      discardEl.style.display = 'none';
    }

    nameEl.textContent = name;

    tierEl.textContent = tierLabel;
    tierEl.className = 'card-tier-label ' + (tierLabel.toLowerCase() || '');

    const pct = Math.max(0, Math.min(1, cooldownFraction)) * 100;
    cooldownEl.style.setProperty('--cooldown', `${pct}%`);
  }
}
